/**
 * Structured logging service with support for different log levels
 * and environment-specific behavior
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, any>;
  error?: Error;
  stack?: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private readonly MAX_LOGS = 100;
  private readonly STORAGE_KEY = 'pcnguard_logs';

  constructor() {
    this.loadLogsFromStorage();
  }

  /**
   * Get current environment (development/production)
   */
  private getEnvironment(): string {
    return import.meta.env.VITE_ENVIRONMENT || 'development';
  }

  /**
   * Check if we're in development mode
   */
  private isDevelopment(): boolean {
    return this.getEnvironment() === 'development';
  }

  /**
   * Create a log entry
   */
  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, any>,
    error?: Error
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      error,
      stack: error?.stack,
    };
  }

  /**
   * Store log entry in memory and IndexedDB
   */
  private storeLog(entry: LogEntry): void {
    // Add to in-memory array
    this.logs.push(entry);

    // Keep only last MAX_LOGS entries
    if (this.logs.length > this.MAX_LOGS) {
      this.logs = this.logs.slice(-this.MAX_LOGS);
    }

    // Persist to localStorage (IndexedDB would be better for production)
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.logs));
    } catch (e) {
      // Storage quota exceeded - clear old logs
      this.logs = this.logs.slice(-50);
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.logs));
      } catch (e2) {
        // Still failing - give up on persistence
        console.warn('Failed to persist logs to storage');
      }
    }
  }

  /**
   * Load logs from localStorage
   */
  private loadLogsFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to load logs from storage');
      this.logs = [];
    }
  }

  /**
   * Format log entry for console output
   */
  private formatForConsole(entry: LogEntry): string {
    const { timestamp, level, message, context } = entry;
    const time = new Date(timestamp).toLocaleTimeString();
    const contextStr = context ? `\n${JSON.stringify(context, null, 2)}` : '';
    return `[${time}] ${level.toUpperCase()}: ${message}${contextStr}`;
  }

  /**
   * Output to console based on log level
   */
  private outputToConsole(entry: LogEntry): void {
    const formatted = this.formatForConsole(entry);

    switch (entry.level) {
      case 'debug':
        console.debug(formatted);
        break;
      case 'info':
        console.info(formatted);
        break;
      case 'warn':
        console.warn(formatted);
        if (entry.error) console.warn(entry.error);
        break;
      case 'error':
        console.error(formatted);
        if (entry.error) console.error(entry.error);
        break;
    }
  }

  /**
   * Send log to remote logging service (Sentry, LogRocket, etc.)
   * Only in production
   */
  private async sendToRemote(_entry: LogEntry): Promise<void> {
    if (this.isDevelopment()) return;

    // TODO: Integrate with Sentry in Phase 8
    // For now, we'll just store locally
    // Example:
    // if (entry.level === 'error' && entry.error) {
    //   Sentry.captureException(entry.error, {
    //     contexts: { custom: entry.context },
    //   });
    // }
  }

  /**
   * Main logging method
   */
  private log(
    level: LogLevel,
    message: string,
    context?: Record<string, any>,
    error?: Error
  ): void {
    const entry = this.createLogEntry(level, message, context, error);

    // Always store in memory
    this.storeLog(entry);

    // Console output (always in dev, errors/warns in prod)
    if (this.isDevelopment() || level === 'error' || level === 'warn') {
      this.outputToConsole(entry);
    }

    // Remote logging (production only, async)
    this.sendToRemote(entry).catch((e) => {
      console.error('Failed to send log to remote service', e);
    });
  }

  /**
   * Debug level logging (development only)
   */
  public debug(message: string, context?: Record<string, any>): void {
    if (this.isDevelopment()) {
      this.log('debug', message, context);
    }
  }

  /**
   * Info level logging
   */
  public info(message: string, context?: Record<string, any>): void {
    this.log('info', message, context);
  }

  /**
   * Warning level logging
   */
  public warn(message: string, contextOrError?: Record<string, any> | Error): void {
    if (contextOrError instanceof Error) {
      this.log('warn', message, undefined, contextOrError);
    } else {
      this.log('warn', message, contextOrError);
    }
  }

  /**
   * Error level logging
   */
  public error(message: string, error?: Error | unknown, context?: Record<string, any>): void {
    // Handle different error types
    let actualError: Error | undefined;

    if (error instanceof Error) {
      actualError = error;
    } else if (error && typeof error === 'object') {
      // Try to extract useful info from unknown error objects
      const errorObj = error as any;
      actualError = new Error(errorObj.message || String(error));
      if (errorObj.stack) actualError.stack = errorObj.stack;
    } else if (error) {
      actualError = new Error(String(error));
    }

    this.log('error', message, context, actualError);
  }

  /**
   * Get all stored logs (for support/debugging)
   */
  public getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs filtered by level
   */
  public getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter((log) => log.level === level);
  }

  /**
   * Clear all stored logs
   */
  public clearLogs(): void {
    this.logs = [];
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear logs from storage');
    }
  }

  /**
   * Export logs as JSON (for support tickets)
   */
  public exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Download logs as a file
   */
  public downloadLogs(): void {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pcnguard-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for external use
export type { LogEntry, LogLevel };
